import * as THREE from 'three';

type Disposable = THREE.BufferGeometry | THREE.Material;

/**
 * Placeholder on-foot avatar: a blocked-out suited figure, not final art.
 *
 * Built in the same local convention as everything else — feet at the
 * group's origin, +Y up, +Z forward — so the caller can position and orient
 * it exactly like `ShipModel.root`: set `.position` to the camera-relative
 * offset and `.quaternion` to the body-frame orientation directly, no extra
 * transform here.
 */
export class CharacterModel {
  readonly root = new THREE.Group();
  private readonly disposables: Disposable[] = [];

  constructor() {
    this.root.name = 'character-model';
    const suit = this.track(
      new THREE.MeshStandardMaterial({ color: 0xd8d2c4, roughness: 0.75, metalness: 0.08 }),
    );
    const visor = this.track(
      new THREE.MeshStandardMaterial({ color: 0x1a2226, roughness: 0.2, metalness: 0.6 }),
    );
    const accent = this.track(
      new THREE.MeshStandardMaterial({ color: 0xc06a2c, roughness: 0.55, metalness: 0.1 }),
    );

    const torsoGeo = this.track(new THREE.CapsuleGeometry(0.22, 0.55, 4, 10));
    const torso = new THREE.Mesh(torsoGeo, suit);
    torso.position.set(0, 1.22, 0);
    this.root.add(torso);

    const beltGeo = this.track(new THREE.CylinderGeometry(0.235, 0.235, 0.08, 10));
    const belt = new THREE.Mesh(beltGeo, accent);
    belt.position.set(0, 0.95, 0);
    this.root.add(belt);

    const headGeo = this.track(new THREE.SphereGeometry(0.15, 12, 10));
    const head = new THREE.Mesh(headGeo, suit);
    head.position.set(0, 1.68, 0);
    this.root.add(head);

    const visorGeo = this.track(new THREE.SphereGeometry(0.1, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.6));
    const visorMesh = new THREE.Mesh(visorGeo, visor);
    visorMesh.position.set(0, 1.66, 0.09);
    visorMesh.rotation.x = -Math.PI * 0.5;
    this.root.add(visorMesh);

    const legGeo = this.track(new THREE.CapsuleGeometry(0.1, 0.75, 4, 8));
    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(legGeo, suit);
      leg.position.set(0.13 * side, 0.5, 0);
      this.root.add(leg);
    }

    const armGeo = this.track(new THREE.CapsuleGeometry(0.075, 0.6, 4, 8));
    for (const side of [-1, 1]) {
      const arm = new THREE.Mesh(armGeo, suit);
      arm.position.set(0.34 * side, 1.18, 0);
      this.root.add(arm);
    }

    this.root.frustumCulled = false;
  }

  private track<T extends Disposable>(value: T): T {
    this.disposables.push(value);
    return value;
  }

  dispose(): void {
    for (const d of this.disposables) d.dispose();
  }
}
